// CompareRuns
//
// Side-by-side comparison of two past runs:
// - Selected AI model
// - Conversation transcript
// - Execution results
// - Accuracy and F1 metrics

import React, {
  useContext,
  useEffect,
  useState,
} from "react";
import {
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import ReactMarkdown from "react-markdown";

import runService from "../../services/runService";
import { ChatContext } from "../editor/ChatContext";
import { getModelDisplayName } from "../../utils/util.jsx";

const formatPercentageMetric = (
  value,
  decimals = 1
) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return "N/A";
  }

  return `${(numericValue * 100).toFixed(decimals)}%`;
};

const ResultsCard = ({ run }) => (
  <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2.5 text-sm text-[#475569] space-y-1">
    <p>
      <span className="font-semibold text-[#0F172A]">
        Status:
      </span>{" "}
      {run.status || "Unknown"}
    </p>

    <p>
      <span className="font-semibold text-[#0F172A]">
        Epochs:
      </span>{" "}
      {run.epochs != null ? run.epochs : "N/A"}
    </p>

    <p>
      <span className="font-semibold text-[#0F172A]">
        Batch size:
      </span>{" "}
      {run.batch_size != null
        ? run.batch_size
        : "N/A"}
    </p>

    <p>
      <span className="font-semibold text-[#0F172A]">
        Train accuracy:
      </span>{" "}
      {formatPercentageMetric(run.train_accuracy)}
    </p>

    <p>
      <span className="font-semibold text-[#0F172A]">
        Validation accuracy:
      </span>{" "}
      {formatPercentageMetric(run.val_accuracy)}
    </p>

    <p>
      <span className="font-semibold text-[#0F172A]">
        F1 score:
      </span>{" "}
      {formatPercentageMetric(run.f1_score)}
    </p>

    {run.f1_score == null && (
      <p className="text-xs text-[#94A3B8]">
        F1 score was not available for this run.
      </p>
    )}

    {run.stdout && (
      <div>
        <p className="font-semibold text-[#0F172A] mt-2">
          Output
        </p>

        <pre className="whitespace-pre-wrap text-xs bg-white border border-[#E2E8F0] rounded-lg p-2 max-h-40 overflow-auto">
          {run.stdout}
        </pre>
      </div>
    )}

    {run.stderr && (
      <div>
        <p className="font-semibold text-red-700 mt-2">
          Errors
        </p>

        <pre className="whitespace-pre-wrap text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2 max-h-40 overflow-auto">
          {run.stderr}
        </pre>
      </div>
    )}
  </div>
);

const RunColumn = ({
  run,
  availableModels,
}) => (
  <div className="bg-white border border-[#E2E8F0] rounded-3xl p-5 sm:p-6 shadow-sm space-y-4">
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">
        Model
      </p>

      <h2 className="text-lg font-bold text-[#003153]">
        {getModelDisplayName(
          availableModels,
          run.selected_model
        )}
      </h2>

      <p className="text-xs text-[#94A3B8]">
        {run.created_at
          ? new Date(
              run.created_at
            ).toLocaleString()
          : "Date unavailable"}
      </p>
    </div>

    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-[#64748B] mb-2">
        Conversation
      </p>

      <div className="space-y-2 max-h-80 overflow-auto">
        {(run.messages || []).length === 0 ? (
          <p className="text-sm text-[#94A3B8]">
            No messages in this run.
          </p>
        ) : (
          run.messages.map((message, index) => (
            <div
              key={`${message.created_at || "message"}-${index}`}
              className={`p-2.5 rounded-lg text-xs ${
                message.role === "user"
                  ? "bg-gradient-to-br from-sky-50 to-blue-50 border border-sky-100"
                  : "bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-100"
              }`}
            >
              <span
                className={`block font-bold uppercase tracking-wide mb-1 ${
                  message.role === "user"
                    ? "text-sky-700"
                    : "text-emerald-700"
                }`}
              >
                {message.role === "user"
                  ? "You"
                  : "AI"}
              </span>

              <div className="prose prose-sm max-w-none text-slate-700">
                <ReactMarkdown>
                  {message.content || ""}
                </ReactMarkdown>
              </div>
            </div>
          ))
        )}
      </div>
    </div>

    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-[#64748B] mb-2">
        Results
      </p>

      <ResultsCard run={run} />
    </div>
  </div>
);

const CompareRuns = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { availableModels } = useContext(
    ChatContext
  );

  const [runA, setRunA] = useState(null);
  const [runB, setRunB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const runAId = searchParams.get("runA");
  const runBId = searchParams.get("runB");

  useEffect(() => {
    const fetchRuns = async () => {
      const userId =
        sessionStorage.getItem("userId");

      const sessionToken =
        sessionStorage.getItem("session_token");

      if (!runAId || !runBId) {
        setError(
          "Select two runs to compare from a codebase's Run History."
        );
        setLoading(false);
        return;
      }

      if (!userId || !sessionToken) {
        setError(
          "Your login session is missing. Please sign in again."
        );
        setLoading(false);
        return;
      }

      try {
        const [firstRun, secondRun] =
          await Promise.all([
            runService.getRun(
              runAId,
              userId,
              sessionToken
            ),
            runService.getRun(
              runBId,
              userId,
              sessionToken
            ),
          ]);

        setRunA(firstRun);
        setRunB(secondRun);
      } catch (requestError) {
        console.error(
          "Error loading runs for comparison:",
          requestError
        );

        setError(
          "Unable to load one or both runs."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchRuns();
  }, [runAId, runBId]);

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center bg-[#F7FAFC] text-[#0F172A] font-[Manrope]">
        <p className="text-sm sm:text-base text-[#64748B]">
          Loading runs...
        </p>
      </div>
    );
  }

  if (error || !runA || !runB) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center gap-3 bg-[#F7FAFC] text-[#0F172A] font-[Manrope]">
        <p className="text-sm sm:text-base text-[#64748B]">
          {error ||
            "Unable to display the selected runs."}
        </p>

        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-sm font-medium px-4 py-2 rounded-xl border border-[#E2E8F0] bg-white hover:bg-[#E2E8F0] transition"
        >
          ← Back
        </button>
      </div>
    );
  }

  const sameModel =
    runA.selected_model === runB.selected_model;

  return (
    <div className="min-h-screen bg-[#F7FAFC] text-[#0F172A] font-[Manrope]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between gap-3 mb-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center rounded-xl border border-[#E2E8F0] bg-white px-3 py-2 text-xs sm:text-sm font-medium text-[#0F172A] shadow-sm hover:bg-[#E2E8F0] transition"
          >
            ← Back
          </button>
        </div>

        <div className="rounded-3xl bg-gradient-to-r from-[#0EA5E9] via-[#38BDF8] to-[#6366F1] px-5 sm:px-7 py-5 mb-6 shadow-md">
          <p className="text-xs font-semibold uppercase tracking-wide text-sky-950/90">
            {sameModel
              ? "Results vs Results"
              : "Model vs Model"}
          </p>

          <h1 className="text-xl sm:text-2xl font-extrabold text-white">
            {sameModel
              ? `Same model: ${getModelDisplayName(
                  availableModels,
                  runA.selected_model
                )}`
              : `${getModelDisplayName(
                  availableModels,
                  runA.selected_model
                )} vs ${getModelDisplayName(
                  availableModels,
                  runB.selected_model
                )}`}
          </h1>

          <p className="text-xs sm:text-sm text-white/90 mt-2">
            Compare execution status, accuracy,
            F1 score, and output from both runs.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <RunColumn
            run={runA}
            availableModels={availableModels}
          />

          <RunColumn
            run={runB}
            availableModels={availableModels}
          />
        </div>
      </div>
    </div>
  );
};

export default CompareRuns;